import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { StoryReader } from '../components/StoryReader';
import { getStoryById, incrementReadCount } from '../services/storyService';
import { Story } from '../types/Story';
import { Button } from '../components/common/Button';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../constants/colors';

/* ─── Animations ─── */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const pageFlip = keyframes`
  0%, 100% { transform: rotateY(0deg); }
  50% { transform: rotateY(20deg); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

/* ─── Layout ─── */

const ReadStoryContainer = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: 24px;
  animation: ${fadeIn} 0.4s ease-out;
  
  @media (max-width: 576px) {
    padding: 16px;
  }
`;

/* ─── Loading ─── */

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
`;

const BookLoader = styled.div`
  position: relative;
  width: 64px;
  height: 52px;
  margin-bottom: 20px;
  animation: ${float} 2s ease-in-out infinite;
`;

const BookBase = styled.div`
  width: 56px;
  height: 40px;
  background: ${COLORS.PRIMARY};
  border-radius: 4px 8px 8px 4px;
  position: absolute;
  bottom: 0;
  left: 4px;
  box-shadow: 0 4px 12px ${COLORS.PRIMARY}33;
`;

const BookPage = styled.div`
  width: 26px;
  height: 36px;
  background: white;
  border-radius: 0 4px 4px 0;
  position: absolute;
  bottom: 2px;
  left: 30px;
  transform-origin: left center;
  animation: ${pageFlip} 1.2s ease-in-out infinite;
  border: 1px solid #e9ecef;
`;

const BookSpine = styled.div`
  width: 4px;
  height: 40px;
  background: ${COLORS.PRIMARY_DARK || '#3b5bdb'};
  border-radius: 2px 0 0 2px;
  position: absolute;
  bottom: 0;
  left: 4px;
`;

const LoadingText = styled.p`
  font-size: 1rem;
  color: #868e96;
  margin: 0;
  letter-spacing: 0.01em;
`;

const LoadingDots = styled.span`
  &::after {
    content: '';
    animation: dots 1.5s steps(4, end) infinite;
  }
  
  @keyframes dots {
    0% { content: ''; }
    25% { content: '.'; }
    50% { content: '..'; }
    75% { content: '...'; }
    100% { content: ''; }
  }
`;

/* ─── Error ─── */

const ErrorContainer = styled.div`
  background: linear-gradient(135deg, #fff5f5 0%, #fff0f0 100%);
  border: 1px solid #fecdd3;
  border-radius: 16px;
  padding: 48px 32px;
  text-align: center;
  animation: ${fadeIn} 0.4s ease-out;
`;

const ErrorIcon = styled.div`
  margin-bottom: 16px;
  
  svg {
    width: 56px;
    height: 56px;
    color: #ffa8a8;
  }
`;

const ErrorTitle = styled.h2`
  font-size: 1.35rem;
  font-weight: 600;
  color: #c92a2a;
  margin: 0 0 8px 0;
`;

const ErrorMessage = styled.p`
  font-size: 0.95rem;
  color: #868e96;
  margin: 0 0 24px 0;
  line-height: 1.5;
`;

const BackButton = styled(Button)`
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

/* ─── Component ─── */

export const ReadStoryPage: React.FC = () => {
  const { storyId } = useParams<{ storyId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [story, setStory] = useState<Story | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const readCountIncrementedRef = useRef<boolean>(false);  

  useEffect(() => {
    const fetchStory = async () => {
      if (!storyId) {
        setError(t('readStory.missingId', { defaultValue: 'Story ID is missing' }));
        setLoading(false);
        return;
      }
      
      try {
        const response = await getStoryById(storyId);
        
        if (response.success && response.data) {
          setStory(response.data);
        } else {
          setError(response.error || t('readStory.loadFailed', { defaultValue: 'Failed to load story' }));
        }
      } catch (err) {
        setError(t('readStory.unexpectedError', { defaultValue: 'An unexpected error occurred' }));
      } finally {
        setLoading(false);
      }
    };
    
    fetchStory();
    readCountIncrementedRef.current = false;
  }, [storyId]);

  useEffect(() => {
    if (!story) return;
    
    return () => {
      handleIncrementReadCount();
    };
  }, [story]);

  const handleIncrementReadCount = async () => {
    if (storyId && !readCountIncrementedRef.current) {
      readCountIncrementedRef.current = true;
      
      try {
        const response = await incrementReadCount(storyId);
        if (response.success) {
          console.log(`Read count incremented: ${response.data?.readCount}`);
        } else {
          console.error('Failed to increment read count:', response.error);
        }
      } catch (error) {
        console.error('Error incrementing read count:', error);
      }
    }
  };
  
  if (loading) {
    return (
      <ReadStoryContainer>
        <LoadingContainer>
          <BookLoader>
            <BookBase />
            <BookSpine />
            <BookPage />
          </BookLoader>
          <LoadingText>
            {t('readStory.loading', { defaultValue: 'Opening your story' })}
            <LoadingDots />
          </LoadingText>
        </LoadingContainer>
      </ReadStoryContainer>
    );
  }
  
  if (error || !story) {
    return (
      <ReadStoryContainer>
        <ErrorContainer>
          <ErrorIcon>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              <line x1="9" y1="9" x2="15" y2="15" />
              <line x1="15" y1="9" x2="9" y2="15" />
            </svg>
          </ErrorIcon>
          <ErrorTitle>{t('readStory.errorTitle', { defaultValue: 'Oops!' })}</ErrorTitle>
          <ErrorMessage>
            {error || t('readStory.notFound', { defaultValue: 'We couldn\'t find this story.' })}
          </ErrorMessage>
          <BackButton variant="primary" onClick={() => navigate('/library')}>
            {t('readStory.backToLibrary', { defaultValue: 'Back to Library' })}
          </BackButton>
        </ErrorContainer>
      </ReadStoryContainer>
    );
  }
  
  return (
    <ReadStoryContainer>
      <StoryReader story={story} />
    </ReadStoryContainer>
  );
};